import React, {
  ReactNode,
  useMemo,
} from 'react';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import MuiIconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  notEmptyString,
} from '@populateui/utils';
import IconButton from 'core/components/IconButton';
import Skeleton from 'core/components/Skeleton';
import DownloadIcon from 'core/icons/download';
import EyeIcon from 'core/icons/eye';
import Button from 'pages/Dashboard/components/Button';
import Row from 'pages/Dashboard/components/Row';
import Section from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/Section';
import Spinner from 'pages/Dashboard/components/Spinner';
import UserName from 'pages/Dashboard/components/UserName';
import type {
  SidePanelComponentProps,
} from 'pages/Dashboard/contexts/sidePanel';
import {
  getDisplayName,
} from 'pages/Dashboard/utils/helper';
import {
  formatDateTime,
} from 'utils/date';
import {
  getErrorMessage,
} from 'utils/errors';
import {
  useDownloadAllRunOutputFiles,
  useDownloadRunOutputFile,
  usePreviewRunOutputFile,
  useRunOutput,
} from 'views/EMR/components/Panel/RunOutputPanel/hooks';
import RunStatusChip from 'views/EMR/components/Panel/RunOutputPanel/RunStatusChip';
import type {
  RunHeader,
  RunOutputFile,
  RunOutputPanelInfo,
  RunOutputSource,
  SummaryTone,
} from 'views/EMR/components/Panel/RunOutputPanel/types';
import {
  formatFileMeta,
} from 'views/EMR/components/Panel/RunOutputPanel/utils';
import {
  getFileIcon,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/columns';
import {
  DEFAULT_CONNECTOR,
} from 'core/constants';

const toneClasses: Record<SummaryTone, string> = {
  Default: '',
  Warning: 'text-amber-700',
  Danger: 'text-red-700',
};

const DEFAULT_ERROR_MESSAGE = 'Could not load the run details.';

type PanelHeaderProps = {
  header: RunHeader;
  onClose?: () => void;
  slots?: SidePanelComponentProps['slots'];
};

function PanelHeader({ header, onClose, slots }: PanelHeaderProps) {
  const {
    title,
    createdOn,
    createdBy,
    state,
  } = header;
  const hasState = !isNil(state);
  const hasCreatedOn = !isNil(createdOn);
  const hasCreatedBy = notEmptyString(getDisplayName(createdBy?.formattedNames));
  const hasSubLine = hasState || hasCreatedOn || hasCreatedBy;

  return (
    <div className="grid grid-cols-[1fr_auto] gap-x-3 px-5 pt-4 pb-3.5 border-b border-gray-200">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="self-center flex items-center">
        {slots?.header}
        {slots?.actions}
        <MuiIconButton
          size="small"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon className="!text-xl text-gray-500" />
        </MuiIconButton>
      </div>
      {hasSubLine && (
        <div className="flex gap-0.5 flex-wrap items-center text-xs text-gray-600">
          {hasState && <RunStatusChip state={state} variant="dot" />}
          {hasState && (hasCreatedOn || hasCreatedBy) && (
            <span className="whitespace-pre">{DEFAULT_CONNECTOR}</span>
          )}
          {hasCreatedOn && <span>{formatDateTime(createdOn)}</span>}
          {hasCreatedOn && hasCreatedBy && (
            <span className="whitespace-pre">{DEFAULT_CONNECTOR}</span>
          )}
          {hasCreatedBy && (
            <UserName
              user={createdBy}
              size="2xs"
              variant="circular"
              className="!w-auto !gap-1.5 font-normal"
            />
          )}
        </div>
      )}
    </div>
  );
}

type FileRowProps = {
  file: RunOutputFile;
  isDownloading: boolean;
  isPreviewing: boolean;
  onDownload: (file: RunOutputFile) => void;
  onPreview: (file: RunOutputFile) => void;
};

function FileRow({
  file,
  isDownloading,
  isPreviewing,
  onDownload,
  onPreview,
}: FileRowProps) {
  const meta = formatFileMeta(file);

  return (
    <div className="grid grid-cols-[36px_1fr_auto] gap-2.5 items-center p-2 border-b border-gray-200 last:border-b-0">
      <div className="w-9 h-9 rounded-md bg-gray-100 grid place-items-center">
        {getFileIcon(file.fileName)}
      </div>
      <div className="min-w-0">
        <div className="text-xs font-semibold break-all">{file.fileName}</div>
        {notEmptyString(meta) && <div className="text-xs text-gray-500">{meta}</div>}
      </div>
      <div className="flex gap-1.5">
        {file.canPreview && (
          <IconButton
            size="small"
            tooltip="Preview"
            aria-label={`Preview ${file.fileName}`}
            disabled={isPreviewing}
            onClick={() => onPreview(file)}
          >
            {isPreviewing
              ? <Spinner size={16} />
              : <EyeIcon className="!w-4 !h-4 text-gray-700" />}
          </IconButton>
        )}
        <IconButton
          size="small"
          tooltip="Download"
          aria-label={`Download ${file.fileName}`}
          disabled={isDownloading}
          onClick={() => onDownload(file)}
        >
          {isDownloading
            ? <Spinner size={16} />
            : <DownloadIcon className="!w-4 !h-4 text-gray-700" />}
        </IconButton>
      </div>
    </div>
  );
}

function BodySkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <Skeleton variant="rounded" className="!h-4 w-1/3" />
      <Skeleton variant="rounded" className="!h-4 w-full" />
      <Skeleton variant="rounded" className="!h-4 w-full" />
      <Skeleton variant="rounded" className="!h-10 w-full" />
      <Skeleton variant="rounded" className="!h-10 w-full" />
    </div>
  );
}

type BodyErrorProps = {
  message: ReactNode;
  onRetry: () => void;
};

function BodyError({ message, onRetry }: BodyErrorProps) {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="text-sm text-red-700">{message}</div>
      <Button
        variant="text"
        color="primary"
        size="small"
        onClick={onRetry}
      >
        Try again
      </Button>
    </div>
  );
}

function toSource(entity: RunOutputPanelInfo): RunOutputSource {
  return entity.kind === 'report'
    ? { kind: 'report', reportId: entity.reportId }
    : { kind: 'statement', bulkPatientStatementId: entity.bulkPatientStatementId };
}

export default function RunOutputPanel({
  entity,
  onClose,
  slots,
}: SidePanelComponentProps<RunOutputPanelInfo>) {
  const source = useMemo(() => toSource(entity), [entity]);
  const isFailed = entity.header.state === 'Failed';
  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useRunOutput(source, { enabled: entity.header.state !== 'InProgress' });
  const {
    mutate: download,
    pendingFileId: downloadingFileId,
  } = useDownloadRunOutputFile(source);
  const {
    preview,
    pendingFileId: previewingFileId,
  } = usePreviewRunOutputFile(source);
  const {
    downloadAll,
    isPending: isDownloadingAll,
  } = useDownloadAllRunOutputFiles(source);

  const files = data?.files ?? [];
  const summaryItems = (data?.summary ?? []).map(({ label, value, tone }) => ({
    label,
    value,
    className: toneClasses[tone],
  }));
  const settingsItems = Object.entries(data?.settings ?? {}).map(([label, value]) => ({ label, value }));

  return (
    <div className="flex flex-col h-full w-[40rem] bg-white text-gray-900">
      <PanelHeader
        header={entity.header}
        onClose={onClose}
        slots={slots}
      />
      <div className="flex-1 overflow-y-auto px-5 pt-4 pb-5 flex flex-col gap-4">
        {isLoading && <BodySkeleton />}
        {isError && (
          <BodyError
            message={getErrorMessage(error) || DEFAULT_ERROR_MESSAGE}
            onRetry={() => {
              refetch();
            }}
          />
        )}
        {!isNil(data) && (
          <>
            {notEmptyString(data.message) && (
              <div
                className={clsx('flex items-start gap-2 px-3 py-2.5 rounded-lg text-xs font-medium', {
                  'text-red-600 bg-red-100': isFailed,
                  'text-gray-700 bg-gray-100': !isFailed,
                })}
              >
                <InfoOutlinedIcon
                  className={clsx('!text-base shrink-0', {
                    '!text-red-600': isFailed,
                    '!text-gray-700': !isFailed,
                  })}
                />
                <p className="min-w-0 break-words">{data.message}</p>
              </div>
            )}
            {summaryItems.length > 0 && (
              <Section title="Summary">
                {summaryItems.map(({ label, value, className }) => (
                  <Row key={label} title={label} className='!pr-3'>
                    <span className={clsx('break-words text-right font-semibold', className)}>{value}</span>
                  </Row>
                ))}
              </Section>
            )}
            {files.length > 0 && (
              <Section title="Files">
                {files.map((file, index) => (
                  <FileRow
                    // Old runs have one file with a null id
                    key={file.reportOutputFileId ?? `first-${index}`}
                    file={file}
                    isDownloading={downloadingFileId === file.reportOutputFileId}
                    isPreviewing={previewingFileId === file.reportOutputFileId}
                    onDownload={({ reportOutputFileId }) => download({ reportOutputFileId })}
                    onPreview={preview}
                  />
                ))}
              </Section>
            )}
            {settingsItems.length > 0 && (
              <Section title="Settings">
                {settingsItems.map(({ label, value }) => (
                  <Row key={label} title={label} className='!pr-3'>
                    <span className="break-words text-right font-semibold">{value}</span>
                  </Row>
                ))}
              </Section>
            )}
          </>
        )}
      </div>
      {files.length > 1 && (
        <div className="border-t border-gray-200 px-5 py-3 flex justify-end">
          <Button
            variant="contained"
            color="primary"
            disabled={isDownloadingAll}
            startIcon={isDownloadingAll
              ? <Spinner size={16} />
              : <DownloadIcon className="!w-4 !h-4" />}
            onClick={() => {
              downloadAll(files);
            }}
          >
            Download all
          </Button>
        </div>
      )}
    </div>
  );
}
